import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { ZodError, ZodFormattedError, ZodIssue } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
export type NestedObject = Record<string, unknown>;
type ErrorObject = {
  [key: string]: string | ErrorObject;
};
export const setNestedValue = <T extends NestedObject>(
  obj: T,
  path: string[],
  value: unknown
): T => {
  const [first, ...rest] = path;

  if (!first) return value as T; // Base case: return the new value

  return {
    ...obj,
    [first]: setNestedValue((obj[first] as NestedObject) || {}, rest, value),
  };
};

const formatZodIssue = (issue: ZodIssue): NestedObject => {
  const { path, message } = issue;
  return setNestedValue({}, path.map(String), message);
};
export const createErrorObject = <T extends object>(
  error: ZodError<T>
): ErrorObject => {
  const formattedErrors: ZodFormattedError<T> = error.format();
  const parseFormattedErrors = (
    formatted: ZodFormattedError<T>
  ): ErrorObject => {
    const result: ErrorObject = {};

    for (const key in formatted) {
      const value = formatted[key as keyof typeof formatted];
      if (key === "_errors" && Array.isArray(value) && value.length > 0) {
        return value[0];
      }

      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const nested = parseFormattedErrors(value as ZodFormattedError<T>);
        if (Object.keys(nested).length > 0) {
          result[key] = nested;
        }
      }
    }

    return result;
  };
  return parseFormattedErrors(formattedErrors);

}

export const formatZodError = <T>(error: ZodError<T>): NestedObject => {


  const { issues, } = error;


  return issues.reduce((acc, issue) => {
    const nestedIssue = formatZodIssue(issue);
    return { ...acc, ...nestedIssue };
  }, {});
};
export const formDataToObject = (formData: FormData): NestedObject => {
  const obj: NestedObject = {};
  for (const [key, value] of formData.entries()) {
    const keys = key.split(".");
    keys.reduce<NestedObject>((acc, k, i) => {
      if (i === keys.length - 1) {
        acc[k] = value === "" || value === null ? undefined : value;
      } else {
        acc[k] = acc[k] || {};
      }
      return acc[k] as NestedObject;
    }, obj);
  }
  return obj;
};
export const replaceNullWithUndefined = <T>(obj: T): T | any => {
  if (obj === null) return undefined;
  if (typeof obj !== 'object' || obj === undefined) return obj;
  const copy = Array.isArray(obj) ? [] : {};
  const typedObj = obj as Record<string, any>;
  Object.keys(typedObj).forEach((key) => {
    // @ts-ignore
    copy[key] = replaceNullWithUndefined(typedObj[key]);
  });

  return copy as T;
}
export function objectsToArray(object: Record<string, unknown>): string[] {
  const result: string[] = [];

  Object.values(object).forEach((value) => {
    if (typeof value === "string") {
      result.push(value);
    } else if (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      result.push(...objectsToArray(value as Record<string, unknown>)); // Récursion.
    }
  });

  return result;
}
export function objectsToString(object: Record<string, unknown>): string {
  return objectsToArray(object).join(" ");
}
export const locales = ['en', 'fr'] as const;
export function parseTracklist(text: string) {
  return text
    .split("\n") // 🔹 Séparer chaque ligne
    .map((line) => {
      const parts = line.split(" - ");
      if (parts.length < 3) return null;
      const trackNumber = parseInt(parts[0].trim(), 10);
      const title = parts[1].trim();
      const artists = parts[2].split(";").map((artist) => artist.trim());
      return { trackNumber, title, artists };
    }).filter((track) => track !== null);
}


